import { useState, useEffect } from 'react';
import { Dayjs } from 'dayjs';
import { BulkOrderItem } from '../../../../app/services/api/orderManagementClient';
import { PredictedDemandModel, podClient, ItemConversion } from '../../../../app/services/api/podClient';
import { isPodEnabledEnvironment } from '../../../../app/services/environment';
import { getInventorySku } from '../../../../utils';

interface SelectedStore {
  storeNumber?: string;
  franchiseName?: string;
}

interface UsePredictedDemandsOptions {
  selectedStore: SelectedStore | null | undefined;
  bulkOrderItems: BulkOrderItem[];
  selectedDate: Dayjs | null;
  bulkOrdersLoadVersion: number;
  logError: (error: unknown) => void;
}

const STORE_CONFIGURATION_CHUNK_SIZE = 10;
const PREDICTION_CHUNK_SIZE = 5;

const getBulkOrderItemNumber = (item: Pick<BulkOrderItem, 'itemSku'>): string => getInventorySku({ sku: item.itemSku });

const getConversionNumber = (packSize?: string): number => {
  if (!packSize) {
    return 1;
  }

  return parseFloat(packSize) || 1;
};

export const usePredictedDemands = ({
  selectedStore,
  bulkOrderItems,
  selectedDate,
  bulkOrdersLoadVersion,
  logError,
}: UsePredictedDemandsOptions) => {
  const [predictedDemands, setPredictedDemands] = useState<Record<string, PredictedDemandModel[]>>({});

  useEffect(() => {
    setPredictedDemands({});
  }, [selectedStore?.storeNumber]);

  useEffect(() => {
    let isActive = true;

    const loadPodEnabledStores = async (franchiseName: string, storeNumbers: string[]) => {
      const results: PromiseSettledResult<{ storeNumber: string; isPodEnabled: boolean }>[] = [];

      for (let index = 0; index < storeNumbers.length; index += STORE_CONFIGURATION_CHUNK_SIZE) {
        const chunk = storeNumbers.slice(index, index + STORE_CONFIGURATION_CHUNK_SIZE);
        const chunkResults = await Promise.allSettled(
          chunk.map(async (storeNumber) => {
            const response = await podClient.getStoreConfiguration(franchiseName, storeNumber);
            return { storeNumber, isPodEnabled: response.isPodEnabled };
          }),
        );

        results.push(...chunkResults);
      }

      return results.reduce<string[]>((enabledStores, result) => {
        if (result.status === 'fulfilled') {
          if (result.value.isPodEnabled) {
            enabledStores.push(result.value.storeNumber);
          }

          return enabledStores;
        }

        logError(result.reason);
        return enabledStores;
      }, []);
    };

    const loadPredictedDemands = async () => {
      if (!isPodEnabledEnvironment() || !selectedDate?.isValid() || !selectedStore?.franchiseName || bulkOrderItems.length === 0) {
        if (isActive) {
          setPredictedDemands({});
        }

        return;
      }

      const franchiseName = selectedStore.franchiseName;
      const podDeliveryDate = selectedDate.format('DD-MM-YYYY');
      const uniqueStores = Array.from(new Set(bulkOrderItems.map(item => item.storeNumber)));
      const enabledStores = await loadPodEnabledStores(franchiseName, uniqueStores);

      if (!isActive) {
        return;
      }

      if (enabledStores.length === 0) {
        setPredictedDemands({});
        return;
      }

      const enabledStoreSet = new Set(enabledStores);
      const itemsByStore = new Map<string, BulkOrderItem[]>();
      bulkOrderItems.forEach((item) => {
        if (!enabledStoreSet.has(item.storeNumber)) {
          return;
        }

        const storeItems = itemsByStore.get(item.storeNumber) ?? [];
        storeItems.push(item);
        itemsByStore.set(item.storeNumber, storeItems);
      });

      const storeRequests = enabledStores
        .map((storeNumber) => {
          const storeItems = itemsByStore.get(storeNumber) ?? [];
          const itemsByNumber = new Map<string, BulkOrderItem>();

          storeItems.forEach((item) => {
            const itemNumber = getBulkOrderItemNumber(item);

            if (!itemNumber || itemsByNumber.has(itemNumber)) {
              return;
            }

            itemsByNumber.set(itemNumber, item);
          });

          const conversions: ItemConversion[] = Array.from(itemsByNumber.entries()).map(([itemNumber, item]) => ({
            itemNumber,
            conversionNumber: getConversionNumber(item.packSize),
            isFrozen: false,
          }));

          if (conversions.length === 0) {
            return null;
          }

          return {
            storeNumber,
            itemNumbers: conversions.map((conversion) => conversion.itemNumber),
            conversions,
          };
        })
        .filter((request): request is { storeNumber: string; itemNumbers: string[]; conversions: ItemConversion[] } => request != null);

      if (storeRequests.length === 0) {
        setPredictedDemands({});
        return;
      }

      const allResults: PromiseSettledResult<{ storeNumber: string; predictions: PredictedDemandModel[] }>[] = [];
      for (let index = 0; index < storeRequests.length; index += PREDICTION_CHUNK_SIZE) {
        const chunk = storeRequests.slice(index, index + PREDICTION_CHUNK_SIZE);
        const chunkResults = await Promise.allSettled(
          chunk.map(async ({ storeNumber, itemNumbers, conversions }) => {
            const predictions = await podClient.getPredictionsForItems(
              franchiseName,
              storeNumber,
              [podDeliveryDate],
              itemNumbers,
              conversions,
            );
            return { storeNumber, predictions };
          }),
        );
        allResults.push(...chunkResults);
      }

      if (!isActive) {
        return;
      }

      const demandsByStore: Record<string, PredictedDemandModel[]> = {};
      allResults.forEach((result) => {
        if (result.status === 'fulfilled') {
          demandsByStore[result.value.storeNumber] = result.value.predictions;
          return;
        }

        logError(result.reason);
      });

      setPredictedDemands(demandsByStore);
    };

    loadPredictedDemands().catch((error) => {
      if (isActive) {
        setPredictedDemands({});
      }

      logError(error);
    });

    return () => {
      isActive = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkOrdersLoadVersion, selectedDate, selectedStore?.franchiseName]);

  return { predictedDemands };
};
